/**
 * Local Font Access（DESIGN.md §2.5 第③条字体来源，之前一直是"后续任务"）。
 *
 * 只有 Chromium 支持、需要用户授权，Firefox/Safari 完全没有——用能力
 * 探测而不是 UA 嗅探来决定要不要显示"从系统字体导入"这个入口，跟
 * `io/gateway/index.ts` 的 `selectGateway`（File System Access 能力矩阵）
 * 同一个模式。
 *
 * 一台机器可能有一两百款字体，每款字节数据几百 KB 到几 MB，`queryLocalFonts()`
 * 本身只给轻量元数据（不含字节），真正的字节要调用方对每个 `FontData`
 * 单独 `blob()` 才会传回来——这天然支持"先列名字，用户点哪个再取那个"
 * 这种按需加载，不用在这一层做任何"只加载一部分"的裁剪。
 */

/** 真实浏览器 `FontData` 的最小子集——够这里用，也方便测试喂假对象。 */
export interface LocalFontHandle {
  readonly family: string;
  readonly fullName: string;
  blob(): Promise<Blob>;
}

interface QueryLocalFontsGlobal {
  queryLocalFonts?: unknown;
}

/** 探测这个浏览器是否支持 Local Font Access。 */
export function isLocalFontAccessAvailable(
  globals: QueryLocalFontsGlobal = globalThis as unknown as QueryLocalFontsGlobal,
): boolean {
  return typeof globals.queryLocalFonts === 'function';
}

export class LocalFontAccessDeniedError extends Error {
  constructor(cause: unknown) {
    super('没有获得系统字体访问权限（用户拒绝了浏览器的授权请求）');
    this.name = 'LocalFontAccessDeniedError';
    this.cause = cause;
  }
}

/**
 * 枚举系统字体族名（去重、按字母排序），不取字节。
 *
 * 一个族名下常有多个字重/斜体的 `FontData`（如"PingFang SC"的 Regular/
 * Bold/Semibold 各一条）——v1 每个族名只取排在最前面那一条，字重/斜体
 * 变体不在这次范围内，是已披露的简化（选中的这条通常就是常规字重）。
 */
export async function queryLocalFontFamilies(
  globals: QueryLocalFontsGlobal = globalThis as unknown as QueryLocalFontsGlobal,
): Promise<LocalFontHandle[]> {
  const query = globals.queryLocalFonts;
  if (typeof query !== 'function') {
    throw new Error('这个浏览器不支持 Local Font Access（仅 Chromium 内核支持）');
  }
  let all: LocalFontHandle[];
  try {
    all = (await query.call(globals)) as LocalFontHandle[];
  } catch (error) {
    throw new LocalFontAccessDeniedError(error);
  }
  const byFamily = new Map<string, LocalFontHandle>();
  for (const font of all) {
    if (!byFamily.has(font.family)) byFamily.set(font.family, font);
  }
  return [...byFamily.values()].sort((a, b) => a.family.localeCompare(b.family));
}
